import { useState, useCallback, useEffect } from "react";
import useGenerateVideo from "../../../../../../hooks/useGenerateVideo";
import useMonitorVideoGeneration from "../../../../../../hooks/useMonitorVideoGeneration";
import { sendErrorNotification } from "../../../../../../library/notification";
import { ExportError } from "../../../../../../types";
import { calculateTotalDurationInFrames } from "../utils/timeline";

type UseVideoGenerationProps = {
  video: any;
  tracks: any[];
  fps: number;
  saveVideo: (config: any, tracks: any[], videoId: string) => Promise<void>;
};

export const useVideoGeneration = ({
  video,
  tracks,
  fps,
  saveVideo,
}: UseVideoGenerationProps) => {
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generationProgress, setGenerationProgress] = useState<number>(0);
  const [videoDownloadUrl, setVideoDownloadUrl] = useState<
    string | undefined
  >();
  const [pollingIntervalId, setPollingIntervalId] =
    useState<NodeJS.Timeout | null>(null);
  const [displayError, setDisplayError] = useState<ExportError | null>(null);

  const { monitorVideoGeneration } = useMonitorVideoGeneration();
  const { downloadVideo } = useGenerateVideo();

  useEffect(() => {
    return () => {
      if (pollingIntervalId) {
        clearInterval(pollingIntervalId);
      }
    };
  }, [pollingIntervalId]);

  const clearPollData = useCallback(
    (intervalId: any, videoDownloadUrl: string | undefined) => {
      setIsGenerating(false);
      clearInterval(intervalId);
      setPollingIntervalId(null);
      setGenerationProgress(0);
      setVideoDownloadUrl(videoDownloadUrl);
    },
    []
  );

  const startPolling = useCallback(async () => {
    const pollInterval = 5000;

    if (pollingIntervalId) {
      clearInterval(pollingIntervalId);
    }

    const intervalId = setInterval(async () => {
      try {
        const data = await monitorVideoGeneration(video.id);

        const { downloadUrl, progress, message } = data;

        if (message) {
          sendErrorNotification(message.body);
          setIsGenerating(false);
          setPollingIntervalId(null);
          clearPollData(intervalId, undefined);
        }

        setGenerationProgress(progress || 0);

        if (downloadUrl) {
          setGenerationProgress(100);
          clearPollData(intervalId, downloadUrl);
          setPollingIntervalId(null);
        }
      } catch (error) {
        clearPollData(intervalId, undefined);
        throw error;
      }
    }, pollInterval);

    setPollingIntervalId(intervalId);

    try {
      const data = await monitorVideoGeneration(video.id);
      setGenerationProgress(data.progress || 0);

      if (data.downloadUrl) {
        setVideoDownloadUrl(data.downloadUrl);
        clearPollData(intervalId, data.downloadUrl);
      }
    } catch (error) {
      sendErrorNotification("Kunne ikke overvåke videogenerering.");
    }
  }, [clearPollData, monitorVideoGeneration, pollingIntervalId, video.id]);

  // 4. The main action function is also part of the hook
  const generateVideo = useCallback(async () => {
    if (!video.id) return;

    setGenerationProgress(0);
    setVideoDownloadUrl(undefined);
    setIsGenerating(true);

    //No saves has been made yet (1 - default)
    if (video?.revisions?.length === 1) {
      const calculatedDuration = calculateTotalDurationInFrames(tracks);

      const config = {
        durationInFrames: calculatedDuration,
        fps,
      };

      await saveVideo(config, tracks, video.id);
    }

    try {
      const download = await downloadVideo(video.id);

      if (download?.error) {
        throw new Error(download.error);
      }

      const { status, downloadUrl } = download?.data as any;

      if (status === "download") {
        setVideoDownloadUrl(downloadUrl);
        setIsGenerating(false);
      } else {
        startPolling();
      }
    } catch (e) {
      setIsGenerating(false);
      const message = e as ExportError;
      setDisplayError(message);
      sendErrorNotification(message.body);
    }
  }, [
    downloadVideo,
    fps,
    saveVideo,
    startPolling,
    tracks,
    video.id,
    video?.revisions?.length,
  ]);

  return {
    isGenerating,
    setIsGenerating,
    generationProgress,
    videoDownloadUrl,
    displayError,
    generateVideo,
  };
};
